import Vue from 'vue';
import axios from '../../plugins/axios';
import indexedDB from '../../plugins/indexedDB';
import { getServiceItemsByIds } from '@/api'
import { getMinMaxTimeString, calculateDeliveryPrice } from '@/js/utils/service-helpers'
import { CART_COMPARISON_KEYS, ORDER_TYPE, DELIVERY_TYPE } from '../../js/utils/vars'
import { getDeviceId, getUserId } from '../../js/utils/service-helpers';

// Функция для получения ключа корзины по партнеру
const getCartKey = (orderType, item) => {
    if (!orderType || !item) return null;
    
    let partnerId = null;
    if (orderType === ORDER_TYPE.RESTAURANT_DELIVERY && item.restaurant_id) {
        partnerId = item.restaurant_id;
    } else if (orderType === ORDER_TYPE.GROCER_DELIVERY && item.grocer_id) {
        partnerId = item.grocer_id;
    } else if (orderType === ORDER_TYPE.ENTITY_DELIVERY && item.entity_id) {
        partnerId = item.entity_id;
    }
    
    return partnerId ? `${orderType}_${partnerId}` : null;
};

// Функция для получения ключа корзины из orderType и partnerId
const getCartKeyFromPartner = (orderType, partnerId) => {
    return partnerId ? `${orderType}_${partnerId}` : null;
};

export default {
    namespaced: true,
    state: {
        // Хранилище корзин по ключам партнеров
        carts: {}, // { '1_123': { items: [], orderType: 1, ... }, '4_456': { ... } }
        activeCartKey: null, // Ключ активной корзины
    },

    mutations: {
        // Инициализация корзины для партнера
        INIT_CART(state, { cartKey }) {
            if (!state.carts[cartKey]) {
                Vue.set(state.carts, cartKey, {
                    items: [],
                    orderType: null,
                    paymentType: null,
                    cutlery: 1,
                    demand: null,
                    items_price: 0,
                    old_items_price: 0,
                    cart_price: 0,
                    old_cart_price: 0,
                    deliveryType: DELIVERY_TYPE.DOOR,
                    deliveryPrice: 0,
                    deliveryPriceByPrice: 0,
                    serviceEntities: [],
                    serviceSchedules: [],
                    total_weight: 0,
                    deliveryTime: 0,
                    deliveryDate: '',
                    deliveryTimeValue: '',
                    deliveryPromotion: 0,
                    deliveryComment: '',
                    customer_comment: '',
                    promocode: '',
                    promocode_price: null,
                    promocode_free_delivery: false,
                    delivery_asap_title: '',
                    delivery_time_title: '',
                    delivery_collect_title: '',
                    delivery_asap_collect_title: '',
                    serviceFee: 0,
                });
            }
        },
        // Установка активной корзины
        SET_ACTIVE_CART(state, cartKey) {
            state.activeCartKey = cartKey;
            if (cartKey && !state.carts[cartKey]) {
                Vue.set(state.carts, cartKey, {
                    items: [],
                    orderType: null,
                    paymentType: null,
                    cutlery: 1,
                    demand: null,
                    items_price: 0,
                    old_items_price: 0,
                    cart_price: 0,
                    old_cart_price: 0,
                    deliveryType: DELIVERY_TYPE.DOOR,
                    deliveryPrice: 0,
                    deliveryPriceByPrice: 0,
                    serviceEntities: [],
                    serviceSchedules: [],
                    total_weight: 0,
                    deliveryTime: 0,
                    deliveryDate: '',
                    deliveryTimeValue: '',
                    deliveryPromotion: 0,
                    deliveryComment: '',
                    customer_comment: '',
                    promocode: '',
                    promocode_price: null,
                    promocode_free_delivery: false,
                    delivery_asap_title: '',
                    delivery_time_title: '',
                    delivery_collect_title: '',
                    delivery_asap_collect_title: '',
                    serviceFee: 0,
                });
            }
        },
        // Удаление корзины
        DELETE_CART(state, cartKey) {
            if (state.carts[cartKey]) {
                state.carts[cartKey] = null;
                delete state.carts[cartKey];
            }
            if (state.activeCartKey === cartKey) {
                // Устанавливаем первую доступную корзину как активную
                const keys = Object.keys(state.carts);
                state.activeCartKey = keys.length > 0 ? keys[0] : null;
            }
        },
        SET_ITEMS(state, { cartKey, items }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].items = items;
            }
        },
        CLEAR(state, { cartKey }) {
            if (state.carts[cartKey]) {
                const cart = state.carts[cartKey];
                cart.items = [];
                cart.orderType = null;
                cart.paymentType = null;
                cart.cutlery = 1;
                cart.items_price = 0;
                cart.old_items_price = 0;
                cart.cart_price = 0;
                cart.old_cart_price = 0;
                cart.deliveryType = DELIVERY_TYPE.DOOR;
                cart.deliveryPrice = 0;
                cart.deliveryPriceByPrice = 0;
                cart.serviceEntities = [];
                cart.serviceSchedules = [];
                cart.total_weight = 0;
                cart.deliveryPromotion = 0;
                cart.deliveryDate = '';
                cart.deliveryTimeValue = '';
                cart.deliveryComment = '';
                cart.customer_comment = '';
                cart.promocode = '';
                cart.promocode_price = null;
                cart.promocode_free_delivery = false;
                cart.delivery_asap_title = '';
                cart.delivery_time_title = '';
                cart.delivery_collect_title = '';
                cart.delivery_asap_collect_title = '';
            }
        },
        ADD_ITEM(state, { cartKey, item }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].items.push(item);
            }
        },
        RESTORE_ITEM(state, { cartKey, index }) {
            if (state.carts[cartKey] && state.carts[cartKey].items[index]) {
                const item = state.carts[cartKey].items[index];
                if(item.quantity === 0) {
                    item.quantity = item.weight_step || 1;
                }
                item.removed = false;
            }
        },
        REMOVE_ITEM(state, { cartKey, index, forced }) {
            if (state.carts[cartKey] && state.carts[cartKey].items[index]) {
                const item = state.carts[cartKey].items[index];
                if(forced || item.deleted || item.removed) {
                    state.carts[cartKey].items.splice(index, 1);
                } else {
                    item.removed = true;
                }
            }
        },
        SET_ITEM_QUANTITY(state, { cartKey, quantity, index }) {
            if (state.carts[cartKey] && state.carts[cartKey].items[index]) {
                const item = state.carts[cartKey].items[index];
            if(quantity > 0) {
                    item.removed = false;
                }
                item.quantity = quantity;
            }
        },
        SET_ORDER_TYPE(state, { cartKey, orderType }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].orderType = orderType;
            }
        },
        SET_PAYMENT_TYPE(state, { cartKey, paymentType }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].paymentType = paymentType;
            }
        },
        SET_CUTLERY(state, { cartKey, cutlery }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].cutlery = cutlery;
            }
        },
        SET_ITEMS_PRICE(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].items_price = Math.round(price * 100) / 100;
            }
        },
        SET_OLD_ITEMS_PRICE(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].old_items_price = Math.round(price * 100) / 100;
            }
        },
        SET_CART_PRICE(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].cart_price = Math.round(price * 100) / 100;
            }
        },
        SET_DELIVERY_TIME(state, { cartKey, time }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].deliveryTime = time;
            }
        },
        SET_OLD_CART_PRICE(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].old_cart_price = Math.round(price * 100) / 100;
            }
        },
        SET_DELIVERY_TYPE(state, payload) {
            // Поддержка старого формата: SET_DELIVERY_TYPE(deliveryType)
            let cartKey, deliveryType;
            if (typeof payload === 'object' && payload !== null && payload.cartKey !== undefined) {
                cartKey = payload.cartKey;
                deliveryType = payload.deliveryType;
            } else {
                // Старый формат: payload - это deliveryType
                cartKey = state.activeCartKey;
                deliveryType = payload;
            }
            
            if (cartKey && state.carts[cartKey]) {
                state.carts[cartKey].deliveryType = deliveryType || DELIVERY_TYPE.DOOR;
            }
        },
        SET_DELIVERY_SUM(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].deliveryPrice = price;
            }
        },
        SET_DELIVERY_PRICE(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].deliveryPriceByPrice = price;
            }
        },
        SET_DELIVERY_PROMOTION_PRICE(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].deliveryPromotion = price;
            }
        },
        SET_SERVICE_FEE(state, { cartKey, amount }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].serviceFee = amount;
            }
        },
        SET_DELIVERY_COMMENT(state, { cartKey, comment }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].deliveryComment = comment;
            }
        },
        SET_CUSTOMER_COMMENT(state, { cartKey, comment }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].customer_comment = comment;
            }
        },
        RECALC_CART_PRICE(state, payload = {}) {
            const cartKey = payload.cartKey || state.activeCartKey;
            if (!cartKey || !state.carts[cartKey]) return;
            const cart = state.carts[cartKey];
            let cart_price = 0;
            let old_cart_price = 0;
            for (var i = 0; i < cart.items.length; i++) {
                if(!cart.items[i].deleted && !cart.items[i].removed) {
                    cart_price += cart.items[i].item_price * cart.items[i].quantity;
                    old_cart_price += (cart.items[i].old_item_price ? cart.items[i].old_item_price : cart.items[i].item_price) * cart.items[i].quantity;
                }
            }
            cart.cart_price = cart_price;
            cart.old_cart_price = old_cart_price;
            cart.items_price = cart_price;
            cart.old_items_price = old_cart_price;
        },
        RECALC_TOTAL_WEIGHT(state, payload = {}) {
            const cartKey = payload.cartKey || state.activeCartKey;
            if (!cartKey || !state.carts[cartKey]) return;
            const cart = state.carts[cartKey];
            var totalWeight = 0;
            if (cart.items.length > 0) {
                for (let k = 0; k < cart.items.length; k++) {
                    if (cart.items[k].weight && !cart.items[k].deleted && !cart.items[k].removed) {
                        totalWeight += (+cart.items[k].quantity * +cart.items[k].weight);
                    }
                }
            }
            cart.total_weight = totalWeight;
        },
        SET_DEMAND(state, { cartKey, demand }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].demand = demand;
            }
        },
        SET_SERVICE_ENTITIES(state, { cartKey, entities }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].serviceEntities = entities;
            }
        },
        SET_SERVICE_SCHEDULES(state, { cartKey, schedules }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].serviceSchedules = schedules;
            }
        },
        SET_TOTAL_WEIGHT(state, { cartKey, weight }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].total_weight = weight;
            }
        },
        SET_PROMOCODE(state, { cartKey, promocode }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].promocode = promocode;
            }
        },
        SET_PROMOCODE_PRICE(state, { cartKey, price }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].promocode_price = price;
            }
        },
        SET_PROMOCODE_FREE_DELIVERY(state, { cartKey, free }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].promocode_free_delivery = free;
            }
        },
        SET_DELIVERY_DATE(state, { cartKey, date }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].deliveryDate = date;
            }
        },
        SET_DELIVERY_TIME_VALUE(state, { cartKey, time }) {
            if (state.carts[cartKey]) {
                state.carts[cartKey].deliveryTimeValue = time;
            }
        },
        SET_DELIVERY_TITLES(state, { cartKey, delivery_asap_title, delivery_time_title, delivery_collect_title, delivery_asap_collect_title }) {
            if (state.carts[cartKey]) {
                if (delivery_asap_title !== undefined) {
                    state.carts[cartKey].delivery_asap_title = delivery_asap_title;
                }
                if (delivery_time_title !== undefined) {
                    state.carts[cartKey].delivery_time_title = delivery_time_title;
                }
                if (delivery_collect_title !== undefined) {
                    state.carts[cartKey].delivery_collect_title = delivery_collect_title;
                }
                if (delivery_asap_collect_title !== undefined) {
                    state.carts[cartKey].delivery_asap_collect_title = delivery_asap_collect_title;
                }
            }
        }
    },
    actions: {
        // Получить активную корзину или создать новую
        getActiveCart({ state, commit }) {
            if (!state.activeCartKey && Object.keys(state.carts).length > 0) {
                commit('SET_ACTIVE_CART', Object.keys(state.carts)[0]);
            }
            return state.activeCartKey ? state.carts[state.activeCartKey] : null;
        },
        // Установить активную корзину
        setActiveCart({ commit }, cartKey) {
            commit('SET_ACTIVE_CART', cartKey);
        },
        // Получить или создать корзину для партнера
        getOrCreateCart({ state, commit }, { orderType, item }) {
            return new Promise((resolve) => {
                const cartKey = getCartKey(orderType, item);
                if (!cartKey) {
                    resolve(null);
                    return;
                }
                
                if (!state.carts[cartKey]) {
                    commit('INIT_CART', { cartKey });
                }
                if (!state.activeCartKey) {
                    commit('SET_ACTIVE_CART', cartKey);
                }
                resolve(cartKey);
            });
        },
        async clear({ state, commit, dispatch }, cartKey = null) {
            const key = cartKey ? cartKey : state.activeCartKey;
            if (key) {
                console.log('clear', key);
                commit('CLEAR', { cartKey: key });
                await dispatch('setCacheData');
            }
        },
        // Удаление конкретной корзины
        async deleteCart({ state, commit, dispatch }, cartKey) {
            if (cartKey && state.carts[cartKey]) {
                commit('DELETE_CART', cartKey);
                await dispatch('setCacheData');
            }
        },
        // Удаление всех корзин
        async clearAllCarts({ state, commit, dispatch }) {
            const cartKeys = Object.keys(state.carts);
            for (const cartKey of cartKeys) {
                commit('DELETE_CART', cartKey);
            }
            await dispatch('setCacheData');
        },
        async setCacheData({ state }) {
            const device_id = await getDeviceId();
            const customer_id = await getUserId();
            if (device_id || customer_id) {
                await axios.post('/front/customer/cart', { 
                    device_id,
                    customer_id,
                    cart: JSON.stringify(state)
                });
                if(!device_id) {
                    await indexedDB.remove('mircity-cart');
                }
            } else {
                await indexedDB.set('mircity-cart', state);
            }
        },
        async getCacheData({ commit, state }) {
            let lsState = null;
            const device_id = await getDeviceId();
            const customer_id = await getUserId();
            if (customer_id) {
                const { data } = await axios.get(`/front/customer/cart/customer/${ customer_id }`);
                lsState = data.cart ? JSON.parse(data.cart) : null;
            } else if (device_id) {
                const { data } = await axios.get(`/front/customer/cart/device/${ device_id }`);
                lsState = data.cart ? JSON.parse(data.cart) : null;
            } else {
                lsState = await indexedDB.get('mircity-cart');
            }
            
            // Миграция старого формата в новый
            if (lsState) {
                if (lsState.items && !lsState.carts) {
                    // Старый формат - конвертируем в новый
                    if (lsState.items.length > 0 && lsState.orderType) {
                        const firstItem = lsState.items[0];
                        const cartKey = getCartKey(lsState.orderType, firstItem);
                        if (cartKey) {
                            commit('INIT_CART', { cartKey });
                            commit('SET_ACTIVE_CART', cartKey);
                lsState.items = lsState.items.filter(item => !item.removed);
                            commit('SET_ORDER_TYPE', { cartKey, orderType: lsState.orderType });
                            commit('SET_PAYMENT_TYPE', { cartKey, paymentType: lsState.paymentType });
                            commit('SET_ITEMS', { cartKey, items: lsState.items });
                            commit('SET_CUTLERY', { cartKey, cutlery: lsState.cutlery });
                            commit('SET_ITEMS_PRICE', { cartKey, price: lsState.items_price });
                            commit('SET_OLD_ITEMS_PRICE', { cartKey, price: lsState.old_items_price });
                            commit('SET_CART_PRICE', { cartKey, price: lsState.cart_price });
                            commit('SET_DELIVERY_TYPE', { cartKey, deliveryType: lsState.deliveryType });
                            commit('SET_DELIVERY_SUM', { cartKey, price: lsState.deliveryPrice });
                            commit('SET_DELIVERY_PRICE', { cartKey, price: lsState.deliveryPriceByPrice });
                            commit('SET_OLD_CART_PRICE', { cartKey, price: lsState.old_cart_price });
                            commit('SET_TOTAL_WEIGHT', { cartKey, weight: lsState.total_weight });
                            commit('SET_DELIVERY_PROMOTION_PRICE', { cartKey, price: lsState.deliveryPromotion });
                            commit('SET_DELIVERY_COMMENT', { cartKey, comment: lsState.deliveryComment });
                            commit('SET_CUSTOMER_COMMENT', { cartKey, comment: lsState.customer_comment });
                            commit('SET_PROMOCODE', { cartKey, promocode: lsState.promocode });
                            commit('SET_PROMOCODE_PRICE', { cartKey, price: lsState.promocode_price });
                        }
                    }
                } else if (lsState.carts) {
                    // Новый формат
                    commit('SET_ACTIVE_CART', lsState.activeCartKey);
                    Object.keys(lsState.carts).forEach(cartKey => {
                        const cart = lsState.carts[cartKey];
                        commit('INIT_CART', { cartKey });
                        cart.items = cart.items.filter(item => !item.removed);
                        commit('SET_ORDER_TYPE', { cartKey, orderType: cart.orderType });
                        commit('SET_PAYMENT_TYPE', { cartKey, paymentType: cart.paymentType });
                        commit('SET_ITEMS', { cartKey, items: cart.items });
                        commit('SET_CUTLERY', { cartKey, cutlery: cart.cutlery });
                        commit('SET_ITEMS_PRICE', { cartKey, price: cart.items_price });
                        commit('SET_OLD_ITEMS_PRICE', { cartKey, price: cart.old_items_price });
                        commit('SET_CART_PRICE', { cartKey, price: cart.cart_price });
                        commit('SET_DELIVERY_TYPE', { cartKey, deliveryType: cart.deliveryType });
                        commit('SET_DELIVERY_SUM', { cartKey, price: cart.deliveryPrice });
                        commit('SET_DELIVERY_PRICE', { cartKey, price: cart.deliveryPriceByPrice });
                        commit('SET_OLD_CART_PRICE', { cartKey, price: cart.old_cart_price });
                        commit('SET_TOTAL_WEIGHT', { cartKey, weight: cart.total_weight });
                        commit('SET_DELIVERY_PROMOTION_PRICE', { cartKey, price: cart.deliveryPromotion });
                        commit('SET_DELIVERY_COMMENT', { cartKey, comment: cart.deliveryComment });
                        commit('SET_CUSTOMER_COMMENT', { cartKey, comment: cart.customer_comment });
                        commit('SET_PROMOCODE', { cartKey, promocode: cart.promocode });
                        commit('SET_PROMOCODE_PRICE', { cartKey, price: cart.promocode_price });
                    });
                }
            }
        },
        setOrderType({ state, commit }, { orderType, cartKey = null }) {
            const key = cartKey || state.activeCartKey;
            if (key) {
                commit('SET_ORDER_TYPE', { cartKey: key, orderType });
            }
        },
        setPaymentType({ state, commit }, { paymentType, cartKey = null }) {
            const key = cartKey || state.activeCartKey;
            if (key) {
                commit('SET_PAYMENT_TYPE', { cartKey: key, paymentType });
            }
        },
        async addItem({ state, commit, dispatch }, { item, orderType, cartKey = null }) {
            let key = cartKey;
            if (!key) {
                key = await dispatch('getOrCreateCart', { orderType, item });
            }
            if (!key) return;
            
            const cart = state.carts[key];
            if (!cart) return;
            
            let founded = false;
            for (var i = 0; i < cart.items.length; i++) {
                if (_comparison(cart.items[i], item)) {
                    const quantity = Math.round((+cart.items[i].quantity + item.quantity) * 1000) / 1000;
                    commit('SET_ITEM_QUANTITY', { cartKey: key, quantity, index: i });
                    founded = true;
                }
            }
            if (!founded) {
                commit('ADD_ITEM', { cartKey: key, item });
            }
            commit('RECALC_TOTAL_WEIGHT', { cartKey: key });
        },
        restoreItem({ state, commit }, { index, cartKey = null }) {
            const key = cartKey || state.activeCartKey;
            if (key) {
                commit('RESTORE_ITEM', { cartKey: key, index });
                commit('RECALC_TOTAL_WEIGHT', { cartKey: key });
            }
        },
        removeItem({ state, commit }, { index, forced = true, cartKey = null }) {
            const key = cartKey || state.activeCartKey;
            if (!key) return;
            
            commit('REMOVE_ITEM', { cartKey: key, index, forced });
            commit('RECALC_TOTAL_WEIGHT', { cartKey: key });
            
            const cart = state.carts[key];
            if (cart && cart.items.filter(item => !item.deleted && !item.removed).length === 0) {
                indexedDB.remove('mircity-pushNotificationShow');
                indexedDB.remove('mircity-updatePushNotificationDB');
            }
        },
        setItemQuantity({ state, commit }, { quantity, index, cartKey = null }) {
            const key = cartKey || state.activeCartKey;
            if (key) {
                commit('SET_ITEM_QUANTITY', { cartKey: key, quantity, index });
                commit('RECALC_TOTAL_WEIGHT', { cartKey: key });
            }
        },
        async setCutlery({ state, commit, dispatch }, { cutlery, cartKey = null }) {
            const key = cartKey || state.activeCartKey;
            if (key) {
                commit('SET_CUTLERY', { cartKey: key, cutlery });
            await dispatch('setCacheData');
            }
        },
        async getServiceEntities({ state, commit }, payload = {}) {
            const { serviceType, cartKey = null } = payload;
            const key = cartKey || state.activeCartKey;
            if (!key || !state.carts[key] || !serviceType) return;
            
            const cart = state.carts[key];
            const serviceIds = getServiceIds(cart.items, serviceType);
            const serviceEntities = await getServiceItemsByIds(serviceIds, serviceType);
            commit('SET_SERVICE_ENTITIES', { cartKey: key, entities: serviceEntities });
        },
        async getServiceEntitiesForAllCarts({ state, dispatch }) {
            const cartKeys = Object.keys(state.carts || {});
            for (const cartKey of cartKeys) {
                const cart = state.carts[cartKey];
                if (!cart || !cart.items || !Array.isArray(cart.items) || cart.items.length === 0) {
                    continue;
                }
                if (!cart.orderType) {
                    continue;
                }

                await dispatch('getServiceEntities', {
                    serviceType: cart.orderType,
                    cartKey,
                });
            }
        },
        async getServiceSchedules({ state, commit }, payload = {}) {
            const { cartKey = null } = payload;
            const key = cartKey || state.activeCartKey;
            if (!key || !state.carts[key]) return;
            
            const cart = state.carts[key];
            const serviceIds = getServiceIds(cart.items, cart.orderType);
            const serviceSchedules = await getServiceItemsByIds(serviceIds, cart.orderType, true);
            commit('SET_SERVICE_SCHEDULES', { cartKey: key, schedules: serviceSchedules });
        },
        async recalculateDeliveryPrice({ state, commit, dispatch }, payload = {}) {
            const cartKey = payload?.cartKey || null;
            const key = cartKey || state.activeCartKey;
            if (!key || !state.carts[key]) return;
            
            const cart = state.carts[key];
            if (!cart?.serviceEntities?.length) {
                return;
            }

            const deliveryMap = {};
            const promotionMap = {};
            let deliverySum = 0;
            let deliveryPrice = 0;
            let deliveryDiscount = 0;
            let deliveryDiscountSum = 0;

            commit('RECALC_CART_PRICE', { cartKey: key });
            
            for (let i = 0; i < cart.serviceEntities.length; i++) {
                const entity = cart.serviceEntities[i];
                if (!entity?.delivery?.id) {
                    continue;
                }
                deliveryMap[entity?.id] = entity?.delivery;

                if (!entity?.promotion?.id) {
                    continue;
                }
                promotionMap[entity?.id] = entity.promotion;
            }
            Object.values(promotionMap).forEach(item => {
                if (item.delivery_type && item.delivery_type === cart.deliveryType && item.discount) {
                    deliveryDiscount = item.discount;
                }
            });

            for (const item of cart.items) {
                if(!item.deleted && !item.removed) {
                    const itemPrice = item.item_price ? item.item_price : +item.price;
                    const itemOldPrice = item.old_item_price ? item.old_item_price : +item.old_price;
                    const newPrice = itemOldPrice - (itemOldPrice * deliveryDiscount / 100);
                    if(newPrice > itemPrice) {
                        deliveryDiscountSum += itemPrice * +item.quantity;
                    } else {
                        deliveryDiscountSum += newPrice * +item.quantity;
                    }
                    deliveryDiscountSum = Math.round(deliveryDiscountSum * 100) / 100;
                }
            }

            Object.values(deliveryMap).forEach(item => {
                const delivery = calculateDeliveryPrice({ delivery: item, orderPrice: cart.cart_price, deliveryType: cart.deliveryType });
                deliverySum += delivery.sum;
                deliveryPrice += delivery.price;
            });

            commit('SET_DELIVERY_PROMOTION_PRICE', { cartKey: key, price: deliveryDiscountSum });
            commit('SET_DELIVERY_PRICE', { cartKey: key, price: cart.promocode_free_delivery ? 0 : deliveryPrice });
            commit('SET_DELIVERY_SUM', { cartKey: key, price: cart.promocode_free_delivery ? 0 : deliverySum });
            await dispatch('setCacheData');
        },
        async fetchServiceFee({ state, commit }) {
            const key = state.activeCartKey;
            if (!key || !state.carts[key]) return;

            try {
                const { data } = await axios.get('/front/default-settings/service-fee');
                if (data && data.enabled) {
                    commit('SET_SERVICE_FEE', { cartKey: key, amount: +data.amount || 49 });
                } else {
                    commit('SET_SERVICE_FEE', { cartKey: key, amount: 0 });
                }
            } catch (e) {
                console.error('[ServiceFee] Error fetching service fee:', e.message);
                commit('SET_SERVICE_FEE', { cartKey: key, amount: 0 });
            }
        },
        async recalculate({ state, commit, dispatch }, payload) {
            const { cartKey = null } = payload || {};
            const key = cartKey || state.activeCartKey;
            if (!key || !state.carts[key]) return;
            
            const cart = state.carts[key];
            let dishTmp = {};
            let item_price = 0;
            let old_item_price = 0;
            for (var i = 0; i < cart.items.length; i++) {
                if (cart.items[i].restaurant_id) {
                    dishTmp = await axios.get(`/front/restaurants/${cart.items[i].restaurant_id}/dishes/${cart.items[i].dish_id}`);
                    dishTmp = dishTmp.data;

                    item_price = +dishTmp.price;
                    old_item_price = +dishTmp.old_price;
                    for (let ii = 0; ii < dishTmp.modifiers.length; ii++) {
                        for (let ij = 0; ij < cart.items[i].modifiers.length; ij++) {
                            if (dishTmp.modifiers[ii].id === cart.items[i].modifiers[ij].id) {
                                cart.items[i].modifiers[ij] = dishTmp.modifiers[ii];
                                item_price += +dishTmp.modifiers[ii].price;
                                old_item_price += +dishTmp.modifiers[ii].old_price;
                            }
                        }
                    }
                    for (let ii = 0; ii < dishTmp.ingredients.length; ii++) {
                        for (let ij = 0; ij < cart.items[i].ingredients.length; ij++) {
                            if (dishTmp.ingredients[ii].id === cart.items[i].ingredients[ij].id) {
                                cart.items[i].ingredients[ij] = dishTmp.ingredients[ii];
                                item_price += +dishTmp.ingredients[ii].price;
                                old_item_price += +dishTmp.ingredients[ii].old_price
                            }
                        }
                    }
                    if (dishTmp?.modifierGroups?.length) {
                        for (let g = 0; g < dishTmp.modifierGroups.length; g++) {
                            for (let gm = 0; gm < dishTmp.modifierGroups[g].modifiers.length; gm++) {
                                for (let ij = 0; ij < cart.items[i].modifiers.length; ij++) {
                                    if (dishTmp.modifierGroups[g].modifiers[gm].id === cart.items[i].modifiers[ij].id) {
                                        cart.items[i].modifiers[ij] = dishTmp.modifierGroups[g].modifiers[gm];
                                        item_price += +dishTmp.modifierGroups[g].modifiers[gm].price;
                                        old_item_price += +dishTmp.modifierGroups[g].modifiers[gm].old_price;
                                    }
                                }
                            }
                        }
                    }
                    if (dishTmp?.ingredientGroups?.length) {
                        for (let g = 0; g < dishTmp.ingredientGroups.length; g++) {
                            for (let gm = 0; gm < dishTmp.ingredientGroups[g].ingredients.length; gm++) {
                                for (let ij = 0; ij < cart.items[i].ingredients.length; ij++) {
                                    if (dishTmp.ingredientGroups[g].ingredients[gm].id === cart.items[i].ingredients[ij].id) {
                                        cart.items[i].ingredients[ij] = dishTmp.ingredientGroups[g].ingredients[gm];
                                        item_price += +dishTmp.ingredientGroups[g].ingredients[gm].price;
                                        old_item_price += +dishTmp.ingredientGroups[g].ingredients[gm].old_price;
                                    }
                                }
                            }
                        }
                    }
                    cart.items[i] = {
                        ...cart.items[i],
                        ...{
                            price: dishTmp.price,
                            old_price: dishTmp.old_price,
                            item_price: item_price,
                            old_item_price: old_item_price
                        }
                    }
                }
                if (cart.items[i].entity_id) {
                    dishTmp = await axios.get(`/front/entities/${cart.items[i].entity_id}/entity_products/${cart.items[i].entity_product_id}`);
                    dishTmp = dishTmp.data;

                    item_price = +dishTmp.price;
                    old_item_price = +dishTmp.old_price;
                    for (let ii = 0; ii < dishTmp.entityVariants.length; ii++) {
                        for (let ij = 0; ij < cart.items[i].entityVariants.length; ij++) {
                            if (dishTmp.entityVariants[ii].id === cart.items[i].entityVariants[ij].id) {
                                cart.items[i].entityVariants[ij] = dishTmp.entityVariants[ii];
                                item_price += +dishTmp.entityVariants[ii].price
                                old_item_price += +dishTmp.entityVariants[ii].old_price
                            }
                        }
                    }
                    for (let ii = 0; ii < dishTmp.entityOptions.length; ii++) {
                        for (let ij = 0; ij < cart.items[i].entityOptions.length; ij++) {
                            if (dishTmp.entityOptions[ii].id === cart.items[i].entityOptions[ij].id) {
                                cart.items[i].entityOptions[ij] = dishTmp.entityOptions[ii];
                                item_price += +dishTmp.entityOptions[ii].price
                                old_item_price += +dishTmp.entityOptions[ii].old_price
                            }
                        }
                    }
                    cart.items[i] = {
                        ...cart.items[i],
                        ...{
                            price: dishTmp.price,
                            old_price: dishTmp.old_price,
                            item_price: item_price,
                            old_item_price: old_item_price
                        }
                    }
                }
            }
            commit('RECALC_CART_PRICE', { cartKey: key });
            commit('RECALC_TOTAL_WEIGHT', { cartKey: key });
            await dispatch('setCacheData');
        },

        setDemand({ state, commit }, { demand_id, cartKey = null }) {
            const key = cartKey || state.activeCartKey;
            if (!key || !state.carts[key]) return;
            
            const cart = state.carts[key];
            const serviceEntities = cart.serviceEntities;
            for(const service of serviceEntities) {
                service.delivery.price = service.delivery.all_prices.filter(item => item.demand_id === demand_id);
            }
            commit('SET_DEMAND', { cartKey: key, demand: demand_id });
            commit('SET_SERVICE_ENTITIES', { cartKey: key, entities: serviceEntities });
        },
        // Удаление пустых корзин из store
        async removeEmptyCarts({ state, commit, dispatch }) {
            const keysToDelete = [];
            for (const cartKey of Object.keys(state.carts)) {
                const cart = state.carts[cartKey];
                const activeItems = cart.items.filter(item => !item.deleted && !item.removed);
                if (activeItems.length === 0) {
                    keysToDelete.push(cartKey);
                }
            }
            for (const cartKey of keysToDelete) {
                commit('DELETE_CART', cartKey);
            }
            if (keysToDelete.length > 0) {
                await dispatch('setCacheData');
            }
        },
    },
    getters: {
        // Получить активную корзину
        activeCart(state) {
            return state.activeCartKey ? state.carts[state.activeCartKey] : null;
        },
        // Получить все корзины
        allCarts(state) {
            return state.carts;
        },
        // Получить список ключей корзин
        cartKeys(state) {
            return Object.keys(state.carts);
        },
        // Получить items активной корзины
        items(state, getters) {
            const cart = getters.activeCart;
            return (cart && cart.items && Array.isArray(cart.items)) ? cart.items : [];
        },
        // Получить demand активной корзины
        demand(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.demand : null;
        },
        // Получить orderType активной корзины
        orderType(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.orderType : null;
        },
        // Получить paymentType активной корзины
        paymentType(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.paymentType : null;
        },
        // Получить cutlery активной корзины
        cutlery(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.cutlery : 1;
        },
        // Получить items_price активной корзины
        items_price(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.items_price : 0;
        },
        // Получить old_items_price активной корзины
        old_items_price(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.old_items_price : 0;
        },
        // Получить cart_price активной корзины
        cart_price(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.cart_price : 0;
        },
        // Получить old_cart_price активной корзины
        old_cart_price(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.old_cart_price : 0;
        },
        // Получить deliveryType активной корзины
        deliveryType(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.deliveryType : DELIVERY_TYPE.DOOR;
        },
        // Получить deliveryPrice активной корзины
        deliveryPrice(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.deliveryPrice : 0;
        },
        // Получить deliveryPriceByPrice активной корзины
        deliveryPriceByPrice(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.deliveryPriceByPrice : 0;
        },
        // Получить serviceFee активной корзины
        serviceFee(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.serviceFee : 0;
        },
        // Получить serviceEntities активной корзины
        serviceEntities(state, getters) {
            const cart = getters.activeCart;
            return (cart && cart.serviceEntities && Array.isArray(cart.serviceEntities)) ? cart.serviceEntities : [];
        },
        // Получить serviceSchedules активной корзины
        serviceSchedules(state, getters) {
            const cart = getters.activeCart;
            return (cart && cart.serviceSchedules && Array.isArray(cart.serviceSchedules)) ? cart.serviceSchedules : [];
        },
        // Получить total_weight активной корзины
        total_weight(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.total_weight : 0;
        },
        // Получить deliveryTime активной корзины
        deliveryTime(state, getters) {
            const cart = getters.activeCart;
            if (cart?.serviceEntities?.length) {
                const service = cart.serviceEntities[0];
                const time = getMinMaxTimeString(
                    service.delivery_time,
                    service.delivery_time_hour,
                    service.max_delivery_time,
                    service.max_delivery_time_hour
                );

                if (time?.length > 14) {
                    return `до ${time.split('-')[1].trim()}`;
                }
                return time;
            }
            return cart ? cart.deliveryTime : 0;
        },
        // Получить deliveryPromotion активной корзины
        deliveryPromotion(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.deliveryPromotion : 0;
        },
        // Получить deliveryDate активной корзины
        deliveryDate(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.deliveryDate : '';
        },
        // Получить deliveryTimeValue активной корзины
        deliveryTimeValue(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.deliveryTimeValue : '';
        },
        // Получить deliveryComment активной корзины
        deliveryComment(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.deliveryComment : '';
        },
        // Получить customer_comment активной корзины
        customer_comment(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.customer_comment : '';
        },
        // Получить promocode активной корзины
        promocode(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.promocode : '';
        },
        // Получить promocode_price активной корзины
        promocode_price(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.promocode_price : null;
        },
        // Получить promocode_free_delivery активной корзины
        promocode_free_delivery(state, getters) {
            const cart = getters.activeCart;
            return cart ? cart.promocode_free_delivery : false;
        },
        count(state, getters) {
            const items = getters.items;
            return items.filter(item => {
                return !item.deleted && !item.removed;
            }).length
        },
        quantity(state, getters) {
            const items = getters.items;
            return items.filter(item => {
                return !item.deleted && !item.removed;
            }).reduce((sum, item) => {
                return sum + item.quantity
            }, 0)
        },
        isEmpty(state, getters) {
            const items = getters.items;
            return items.filter(item => {
                return !item.deleted;
            }).length === 0
        },
        totalWeight(state, getters) {
            return getters.total_weight;
        },
    },
}

const getServiceIds = (serviceItems, serviceType) => {
    let ids = [];
    switch (serviceType) {
        case ORDER_TYPE.RESTAURANT_DELIVERY:
            ids = serviceItems.map(i => i.restaurant_id);
            break;
        case ORDER_TYPE.GROCER_DELIVERY:
            ids = serviceItems.map(i => i.grocer_id);
            break;
        case ORDER_TYPE.ENTITY_DELIVERY:
            ids = serviceItems.map(i => i.entity_id);
            break;
    }
    return ids;
}

const _comparison = (itemA, itemB) => {
    for (var key of CART_COMPARISON_KEYS) {
        if (itemA[key] || itemB[key]) {
            if (JSON.stringify(itemA[key]) != JSON.stringify(itemB[key])) {
                return false;
            }
        }
    }
    return true;
}
